import * as THREE from '../extras/three'
import { ControlPriorities } from '../extras/ControlPriorities'
import { Layers } from '../extras/Layers'
import { XRHud } from '../extras/xrHud'
import { XRHtmlBridge } from '../extras/xrHtmlBridge'

import { System } from './System'

const v1 = new THREE.Vector3()
// Always-visible tracked-controller beam; ring only on an interactive target.
const XR_POINTER_DISTANCE = 10
const XR_BEAM_OFFSET = 0.1
const XR_BEAM_LENGTH = 0.35
// Tapered beam, brighter at the controller and fading toward the cursor.
const XR_BEAM_BASE_WIDTH = 0.004
const XR_BEAM_TIP_WIDTH = 0.0015
const XR_BEAM_TIP_ALPHA = 0.25
const XR_CURSOR_ANGLE = 0.008 // outer radius per metre of distance
const XR_CURSOR_PRESSED_SCALE = 0.7
const XR_HOVER_COLOR = '#ffffff'
const XR_PRESS_COLOR = '#001E78'

/**
 *
 * This system handles pointer events.
 * It handles pointer events while the pointer is locked via reticle raycasting.
 * It handles pointer events while the cursor is being used, via world UI.
 *
 */

export class ClientPointer extends System {
  constructor(world) {
    super(world)
    this.pointerState = new PointerState()
    this.xrHand = 'Right'
    this.xrPressHand = null
    this.xrBlocked = false
    this.xrNeedsRelease = { Left: false, Right: false }
    // Controller showing the teleport arc: its ray is suspended (Meta locomotion rules).
    this.xrSuspendedHand = null
    this.xrSession = null
    this.xrHud = new XRHud(world)
    // Existing HTML panels shown in the HUD (see xrHtmlBridge).
    this.xrHtml = new XRHtmlBridge(world, this.xrHud)
  }

  init({ ui }) {
    this.ui = ui
  }

  start() {
    this.control = this.world.controls.bind({
      priority: ControlPriorities.POINTER,
    })
    // Bind both poses before the first XR input sample.
    this.control.xrLeftRayPose
    this.control.xrRightRayPose
    this.control.xrLeftTrigger
    this.control.xrRightTrigger
    this.world.on('xrSession', this.onXRSession)
  }

  update(delta = 0) {
    if (this.xrSession && !this.world.xr.session) this.onXRSession(null)
    let hit
    let pressed
    let released
    if (this.world.xr.session) {
      if (this.xrSession !== this.world.xr.session) this.onXRSession(this.world.xr.session)
      this.xrHud.sync()
      const source = this.selectXRSource()
      if (!source) {
        // No tracked controller: no selection and no beam, not even while fading.
        this.pointerState.cancel()
        this.hideXRVisual()
        return
      }
      const { ray, trigger } = source
      const dir = v1.set(0, 0, -1).applyQuaternion(ray.quaternion)
      // HUD panels are drawn above the scene, so they win over any world hit.
      hit = this.xrHud.raycast(ray.position, dir, XR_POINTER_DISTANCE)
      let distance = hit?.distance ?? XR_POINTER_DISTANCE
      if (!hit) {
        hit = this.world.stage.raycast(ray.position, dir, undefined, 0, XR_POINTER_DISTANCE)[0]
        // Invisible PhysX colliders also occlude world UI, including the batched
        // Smily meshes which are rendered outside the stage hit octree.
        const blocker = this.world.physics.raycast(ray.position, dir, XR_POINTER_DISTANCE, Layers.environment.group | Layers.prop.group)
        distance = hit?.distance ?? XR_POINTER_DISTANCE
        if (blocker && blocker.distance + 0.015 < distance) {
          distance = blocker.distance
          hit = null
        }
      }
      pressed = !this.xrBlocked && trigger.pressed
      released = !this.xrBlocked && trigger.released
      this.pointerState.update(hit, pressed, released)
      const interactive = this.pointerState.isInteractive()
      const pressing = interactive && trigger.down && this.pointerState.pressedNodes.size > 0
      this.updateXRVisual(ray, distance, interactive, pressing, delta)
      if (released) this.xrPressHand = null
      return
    } else if (this.control.pointer.locked) {
      hit = this.world.stage.raycastReticle()[0]
      pressed = this.control.mouseLeft.pressed
      released = this.control.mouseLeft.released
    } else {
      hit = this.screenHit || this.world.stage.raycastPointer(this.control.pointer.position)[0]
      pressed = this.control.mouseLeft.pressed
      released = this.control.mouseLeft.released
    }
    this.pointerState.update(hit, pressed, released)
  }

  // Shared stick inputs go to UI first: true while `hand` points at an interactive target.
  isXRHandOnUI(hand) {
    return !!this.world.xr.session && this.xrHand === hand && hand !== this.xrSuspendedHand && this.pointerState.isInteractive()
  }

  setXRSuspendedHand(hand) {
    this.xrSuspendedHand = hand || null
  }

  selectXRSource() {
    const usable = hand => this.control[`xr${hand}RayPose`].valid && hand !== this.xrSuspendedHand
    const left = { valid: usable('Left') }
    const right = { valid: usable('Right') }
    for (const hand of ['Left', 'Right']) {
      if (!usable(hand)) this.xrNeedsRelease[hand] = true
      else if (!this.control[`xr${hand}Trigger`].down) this.xrNeedsRelease[hand] = false
    }
    // Keep the press on its original controller until release. Losing it cancels,
    // rather than transferring a held trigger to the other hand.
    if (this.xrPressHand && !usable(this.xrPressHand)) {
      this.pointerState.cancel()
      this.xrPressHand = null
      this.xrBlocked = true
    }
    if (this.xrBlocked) {
      const held = ['Left', 'Right'].some(hand => usable(hand) && this.control[`xr${hand}Trigger`].down)
      if (held) return null
      this.xrBlocked = false
    }
    let hand = this.xrPressHand || this.xrHand
    if (!this.xrPressHand) {
      if (right.valid && !this.xrNeedsRelease.Right && this.control.xrRightTrigger.pressed) hand = 'Right'
      else if (left.valid && !this.xrNeedsRelease.Left && this.control.xrLeftTrigger.pressed) hand = 'Left'
      else if (!usable(hand)) hand = right.valid ? 'Right' : 'Left'
    }
    const ray = this.control[`xr${hand}RayPose`]
    if (!usable(hand) || this.xrNeedsRelease[hand]) return null
    const trigger = this.control[`xr${hand}Trigger`]
    this.xrHand = hand
    if (trigger.pressed) this.xrPressHand = hand
    return { ray, trigger }
  }

  updateXRVisual(ray, distance, interactive, pressing) {
    if (!this.xrVisual) {
      this.xrVisual = new THREE.Group()
      const material = () =>
        new THREE.MeshBasicMaterial({
          color: XR_HOVER_COLOR,
          transparent: true,
          opacity: 0,
          depthTest: false,
          depthWrite: false,
          toneMapped: false,
        })
      this.xrBeamMaterial = material()
      this.xrCursorMaterial = material()
      this.xrCursorMaterial.side = THREE.DoubleSide
      this.xrBeamMaterial.vertexColors = true
      this.xrBeam = new THREE.Mesh(createBeamGeometry(), this.xrBeamMaterial)
      this.xrBeam.rotation.x = Math.PI / 2
      // Unit ring facing the controller (XY plane, ray along -Z), scaled per distance.
      this.xrCursor = new THREE.Mesh(new THREE.RingGeometry(0.6, 1, 32), this.xrCursorMaterial)
      this.xrBeam.renderOrder = this.xrCursor.renderOrder = 1000
      this.xrBeam.frustumCulled = this.xrCursor.frustumCulled = false
      this.xrVisual.add(this.xrBeam, this.xrCursor)
      this.xrVisual.visible = false
      this.xrOpacity = 0
      this.xrDistance = 0
      // Direct scene objects are local-only and are not inserted in the hit octree.
      this.world.stage.scene.add(this.xrVisual)
    }
    // Keep the beam visible in the void. A cursor never implies a selectable
    // target when the hit is non-interactive or occluded.
    this.xrDistance = distance
    this.xrOpacity = 1
    const d = distance
    this.xrCursor.visible = interactive
    this.xrVisual.visible = true
    this.xrVisual.position.copy(ray.position)
    this.xrVisual.quaternion.copy(ray.quaternion)
    // Short beam, cut before a closer target.
    const start = Math.min(XR_BEAM_OFFSET, d * 0.5)
    const length = Math.max(0, Math.min(XR_BEAM_LENGTH, d - start))
    this.xrBeam.visible = length > 0
    this.xrBeam.scale.y = length || 1
    this.xrBeam.position.z = -(start + length / 2)
    this.xrCursor.position.z = -d
    this.xrCursor.scale.setScalar(d * XR_CURSOR_ANGLE * (pressing ? XR_CURSOR_PRESSED_SCALE : 1))
    const color = pressing ? XR_PRESS_COLOR : XR_HOVER_COLOR
    this.xrBeamMaterial.color.set(color)
    this.xrCursorMaterial.color.set(color)
    this.xrBeamMaterial.opacity = this.xrCursorMaterial.opacity = this.xrOpacity
  }

  hideXRVisual() {
    this.xrOpacity = 0
    if (this.xrVisual) this.xrVisual.visible = false
  }

  onXRSession = session => {
    this.pointerState.cancel()
    this.xrSession = session
    this.xrHand = 'Right'
    this.xrPressHand = null
    this.xrBlocked = false
    this.xrSuspendedHand = null
    this.xrNeedsRelease = { Left: !!session, Right: !!session }
    this.screenHit = null
    this.xrHtml.reset()
    this.xrHud.clear()
    if (this.control) this.control.mouseLeft.capture = false
    this.hideXRVisual()
  }

  setScreenHit(screenHit) {
    this.screenHit = screenHit
    // capture all mouse click events if our pointer is interacting with world UI
    this.control.mouseLeft.capture = !!screenHit
  }

  cancelForNode(node) {
    if (this.pointerState.activePath.has(node) || this.pointerState.pressedNodes.has(node)) this.pointerState.cancel()
  }

  destroy() {
    this.world.off('xrSession', this.onXRSession)
    this.pointerState.cancel()
    this.xrHtml.destroy()
    this.xrHud.clear()
    this.xrVisual?.removeFromParent()
    this.xrBeam?.geometry.dispose()
    this.xrCursor?.geometry.dispose()
    this.xrBeamMaterial?.dispose()
    this.xrCursorMaterial?.dispose()
    this.xrVisual = null
    this.control?.release()
    this.control = null
  }
}

function createBeamGeometry() {
  // Top (+Y) becomes the controller end after rotation.x = PI/2, the ray runs along -Z.
  const geometry = new THREE.CylinderGeometry(XR_BEAM_BASE_WIDTH / 2, XR_BEAM_TIP_WIDTH / 2, 1, 8, 1, true)
  const position = geometry.attributes.position
  const colors = new Float32Array(position.count * 4)
  for (let i = 0; i < position.count; i++) {
    colors.set([1, 1, 1, position.getY(i) > 0 ? 1 : XR_BEAM_TIP_ALPHA], i * 4)
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 4))
  return geometry
}

const PointerEvents = {
  ENTER: 'pointerenter',
  LEAVE: 'pointerleave',
  DOWN: 'pointerdown',
  UP: 'pointerup',
  CANCEL: 'pointercancel',
}

const CURSOR_DEFAULT = 'default'

class PointerEvent {
  constructor() {
    this.type = null
    this._propagationStopped = false
  }

  set(type) {
    this.type = type
    this._propagationStopped = false
  }

  stopPropagation() {
    this._propagationStopped = true
  }
}

export class PointerState {
  constructor() {
    this.activePath = new Set()
    this.event = new PointerEvent()
    this.cursor = CURSOR_DEFAULT
    this.pressedNodes = new Set()
    this.pressedTarget = null
  }

  isInteractive() {
    return Array.from(this.activePath).some(node => node.cursor === 'pointer' || node.onPointerDown || node.onPointerUp)
  }

  cancelPress() {
    for (const node of this.pressedNodes) {
      this.event.set(PointerEvents.CANCEL)
      try { node.onPointerCancel?.(this.event) } catch (err) { console.error(err) }
    }
    this.pressedNodes.clear()
    this.pressedTarget = null
  }

  cancel() {
    this.cancelPress()
    this.update(null, false, false)
  }

  update(hit, pointerPressed, pointerReleased) {
    const newPath = hit ? this.getAncestorPath(hit) : []
    const oldPath = Array.from(this.activePath)
    const target = newPath[newPath.length - 1]
    if (this.pressedTarget && this.pressedTarget !== target) this.cancelPress()

    // find divergence point
    let i = 0
    while (i < newPath.length && i < oldPath.length && newPath[i] === oldPath[i]) i++

    // pointer leave events bubble up from leaf
    for (let j = oldPath.length - 1; j >= i; j--) {
      if (oldPath[j].onPointerLeave) {
        this.event.set(PointerEvents.LEAVE)
        try {
          oldPath[j].onPointerLeave?.(this.event)
        } catch (err) {
          console.error(err)
        }
        // if (this.event._propagationStopped) break
      }
      this.activePath.delete(oldPath[j])
    }

    // pointer enter events bubble down from divergence
    for (let j = i; j < newPath.length; j++) {
      if (newPath[j].onPointerEnter) {
        this.event.set(PointerEvents.ENTER)
        try {
          newPath[j].onPointerEnter?.(this.event)
        } catch (err) {
          console.error(err)
        }
        if (this.event._propagationStopped) break
      }
      this.activePath.add(newPath[j])
    }

    // set cursor - check from leaf to root for first defined cursor
    let cursor = CURSOR_DEFAULT
    if (newPath.length > 0) {
      for (let i = newPath.length - 1; i >= 0; i--) {
        if (newPath[i].cursor) {
          cursor = newPath[i].cursor
          break
        }
      }
    }
    if (cursor !== this.cursor) {
      document.body.style.cursor = cursor
      this.cursor = cursor
    }

    // handle pointer down events
    if (pointerPressed) {
      this.cancelPress()
      this.pressedTarget = target
      for (let i = newPath.length - 1; i >= 0; i--) {
        const node = newPath[i]
        if (node.onPointerDown) {
          this.event.set(PointerEvents.DOWN)
          try {
            node.onPointerDown(this.event)
          } catch (err) {
            console.error(err)
          }
          this.pressedNodes.add(node)
          if (this.event._propagationStopped) break
        }
      }
    }

    // handle pointer up events
    if (pointerReleased) {
      const nodes = Array.from(this.pressedNodes)
      // Clear before callbacks: opening/closing a panel can synchronously destroy it.
      this.pressedNodes.clear()
      this.pressedTarget = null
      for (const node of nodes) {
        if (!newPath.includes(node)) continue
        if (node.onPointerUp) {
          this.event.set(PointerEvents.UP)
          try {
            node.onPointerUp(this.event)
          } catch (err) {
            console.error(err)
          }
          if (this.event._propagationStopped) break
        }
      }
      this.pressedNodes.clear()
    }
  }

  getAncestorPath(hit) {
    const path = []
    let node = hit.node?.resolveHit?.(hit) || hit.node
    while (node) {
      path.unshift(node)
      node = node.parent
    }
    return path
  }
}
